import { forwardRef } from 'react';

import { RiCheckboxCircleFill } from 'react-icons/ri';

import { Box, Grid, Input, MantineColor, Paper, PaperProps, Stack } from '@ui';

import classes from './CardSelect.module.css';

export interface CardSelectOption extends Record<string, any> {
  value: string;
  name: string;
  highLight?: MantineColor;
  description?: string;
  features?: {
    name: string;
    value: string;
  }[];
  cost?: number;
}

export interface CardSelectProps extends PaperProps {
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  contentRenderer?: (
    option: CardSelectOption,
    isSelected?: boolean
  ) => React.ReactNode;
  options?: CardSelectOption[];
  borderHighlight?: boolean;
}

const CardSelect = forwardRef<HTMLDivElement, CardSelectProps>(
  (
    { value, error, onChange, contentRenderer, options, borderHighlight, ...rest },
    ref
  ) => {
    // Calculate column span based on number of options
    const getColSpan = () => {
      const optionsCount = options?.length ?? 0;
      if (optionsCount <= 2) return 6;
      if (optionsCount <= 3) return 4;
      if (optionsCount <= 4) return 3;
      if (optionsCount <= 6) return 2;
      return 1; // For many options, use smallest size
    };

    const colSpan = getColSpan();
    return (
      <Stack ref={ref} className={classes.cardSelectRoot}>
        <Grid columns={12} gutter={12} p={0}>
          {options?.map((option) => {
            const isSelected = value === option.value;

            return (
              <Grid.Col key={option.value} span={{ base: 12, sm: colSpan }} px={0}>
                <Paper
                  mod={{ selected: isSelected, 'colored-border': borderHighlight }}
                  key={option.value}
                  onClick={() => onChange?.(option.value)}
                  pos="relative"
                  className={classes.cardSelectPaper}
                  shadow="sm"
                  withBorder
                  {...rest}
                >
                  {isSelected && (
                    <Box pos="absolute" top={8} right={8} c="primary.4">
                      <RiCheckboxCircleFill size={20} />
                    </Box>
                  )}
                  {contentRenderer
                    ? contentRenderer(option, isSelected)
                    : option.title}
                </Paper>
              </Grid.Col>
            );
          })}
        </Grid>
        {error && (
          <Input.Error size="xl" mt="sm">
            {error}
          </Input.Error>
        )}
      </Stack>
    );
  }
);

export { CardSelect };
